import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    ChevronLeft,
    ChevronRight,
    X,
    Maximize2,
    Minimize2,
    Loader2,
    Play,
    Pause,
    AlertTriangle,
    Volume2,
    VolumeX,
    ZoomIn,
    ZoomOut,
    RotateCcw,
    StickyNote,
    Check,
    Search,
    Hand,
    TextCursorInput,
    Copy,
    Plus,
    Sparkles,
    FileText,
} from "lucide-react";
import { toast } from "sonner";

/** Lightweight in-app slideshow for "slideshow" lessons.
 *
 * Rendered through a React Portal to `document.body` so the overlay escapes
 * any parent CSS that would otherwise scope `position: fixed` (transforms,
 * filters, etc. trap fixed elements to the transformed ancestor). Uses
 * `100dvh` so mobile browser chrome doesn't clip the top bar or bottom
 * thumbnail strip.
 *
 * Each slide is a base64 JPEG rendered server-side from a page of the source
 * PDF. We keep all slides in memory for instant prev/next and lazily fetch
 * them once when the dialog first opens.
 *
 * Keyboard:
 *   ← / →       previous / next
 *   Space       next
 *   Home / End  first / last
 *   F           fullscreen toggle
 *   Esc         close
 */
export default function LessonSlideshowViewer({ lesson, open, onClose, onReachEnd }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [slides, setSlides] = useState([]);
    const [idx, setIdx] = useState(0);
    const [isFs, setIsFs] = useState(false);
    const [autoplay, setAutoplay] = useState(false);
    const [vh, setVh] = useState(
        typeof window !== "undefined" ? window.innerHeight : 800
    );
    // Per-page text-to-speech narration state
    const [narration, setNarration] = useState({
        status: "idle", // idle | loading | playing | paused
        page: null, // the slide page number this audio was fetched for
    });
    const audioRef = useRef(null);
    const audioUrlRef = useRef(null);
    // Zoom (1.0 = fit-to-frame). Smaller steps + slower easing for smoother feel.
    const [zoom, setZoom] = useState(1);
    const ZOOM_MIN = 0.5;
    const ZOOM_MAX = 4;
    const ZOOM_STEP = 0.1;
    // Pan offset when zoomed-in (drag to move the slide inside the frame)
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const panRef = useRef({ dragging: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
    // Magnifier (loupe)
    const [magnifier, setMagnifier] = useState(false);
    const [loupe, setLoupe] = useState({ visible: false, x: 0, y: 0, bgX: 0, bgY: 0 });
    const frameRef = useRef(null);
    const LOUPE_SIZE = 180;
    const LOUPE_SCALE = 2.4;
    // Hand tool (pan mode) — enables drag-to-pan at any zoom level; also
    // bumps zoom slightly when activated at fit-to-frame so there's room to pan.
    const [handTool, setHandTool] = useState(false);
    // Snip-text (box-select → OCR → save to notes)
    const [snipMode, setSnipMode] = useState(false);
    const [snipBox, setSnipBox] = useState(null); // {x, y, w, h} while dragging
    const snipDragRef = useRef({ dragging: false, startX: 0, startY: 0 });
    const [snipResult, setSnipResult] = useState(null); // {text, used_ocr, box, loading, error}
    // Viewport rect of the frame captured at snip time — used to anchor the
    // floating popover outside the frame so it never hides behind the image.
    const [snipFrameRect, setSnipFrameRect] = useState(null);
    // Snip extras: summary + TTS
    const [snipSummary, setSnipSummary] = useState(null); // {text, loading, error}
    const [snipViewMode, setSnipViewMode] = useState("raw"); // raw | summary
    const [snipAudio, setSnipAudio] = useState({ status: "idle" }); // idle | loading | playing
    const snipAudioRef = useRef(null);
    const snipAudioUrlRef = useRef(null);
    // Notes
    const [notesOpen, setNotesOpen] = useState(false);
    const [note, setNote] = useState("");
    const [noteLocked, setNoteLocked] = useState(false);
    const [noteStatus, setNoteStatus] = useState("idle"); // idle | saving | saved | error
    const noteSaveTimerRef = useRef(null);
    const rootRef = useRef(null);

    const total = slides.length;
    const current = slides[idx];
    const showThumbs = vh >= 700 && total <= 80;

    // Auto-mark the lesson complete the moment the student reaches the
    // last slide. Skip single-slide decks so they don't auto-complete on
    // open — the manual button handles those.
    useEffect(() => {
        if (!open) return;
        if (total < 2) return;
        if (idx < total - 1) return;
        onReachEnd?.();
    }, [open, idx, total, onReachEnd]);

    // Fetch slides lazily on first open ----------------------------------
    useEffect(() => {
        if (!open || !lesson?.id) return;
        let live = true;
        setLoading(true);
        setError("");
        (async () => {
            try {
                const { data } = await api.get(`/lessons/${lesson.id}/slides`);
                if (!live) return;
                setSlides(data.slides || []);
                setIdx(0);
                if (!data.slides || data.slides.length === 0) {
                    setError(
                        "This slideshow is empty. Ask your instructor to re-run the conversion.",
                    );
                }
            } catch (err) {
                if (!live) return;
                const detail =
                    err?.response?.data?.detail ||
                    "Could not load the slideshow. Please try again.";
                setError(typeof detail === "string" ? detail : "Could not load the slideshow.");
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => {
            live = false;
        };
    }, [open, lesson?.id]);

    // Reset transient state on close + unlock body scroll ----------------
    useEffect(() => {
        if (!open) {
            setAutoplay(false);
            setIsFs(false);
            return undefined;
        }
        // Lock body scroll while the overlay is open
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    // Track viewport height so we can hide thumbs on short screens
    useEffect(() => {
        if (!open) return;
        const onResize = () => setVh(window.innerHeight);
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [open]);

    // Keyboard handlers --------------------------------------------------
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            // Don't hijack keys while the user is typing in notes
            const tgt = e.target;
            if (
                tgt &&
                (tgt.tagName === "TEXTAREA" ||
                    tgt.tagName === "INPUT" ||
                    tgt.isContentEditable)
            ) {
                if (e.key !== "Escape") return;
            }
            if (e.key === "ArrowRight" || e.key === "PageDown") {
                e.preventDefault();
                setIdx((i) => Math.min(total - 1, i + 1));
            } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
                e.preventDefault();
                setIdx((i) => Math.max(0, i - 1));
            } else if (e.key === "Home") {
                setIdx(0);
            } else if (e.key === "End") {
                setIdx(Math.max(0, total - 1));
            } else if (e.key === " ") {
                e.preventDefault();
                setIdx((i) => (i < total - 1 ? i + 1 : i));
            } else if (e.key === "Escape") {
                onClose?.();
            } else if (e.key === "f" || e.key === "F") {
                toggleFullscreen();
            } else if (e.key === "+" || e.key === "=") {
                e.preventDefault();
                setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
            } else if (e.key === "-" || e.key === "_") {
                e.preventDefault();
                setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
            } else if (e.key === "0") {
                setZoom(1);
            } else if (e.key === "m" || e.key === "M") {
                setMagnifier((v) => !v);
            } else if (e.key === "h" || e.key === "H") {
                setHandTool((v) => {
                    const nv = !v;
                    // When turning ON the hand tool at fit-to-frame, nudge zoom
                    // so there's actually room to pan.
                    if (nv) setZoom((z) => (z <= 1 ? 1.3 : z));
                    return nv;
                });
            } else if (e.key === "s" || e.key === "S") {
                setSnipMode((v) => !v);
                setSnipResult(null);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, total, onClose]);

    // Autoplay -----------------------------------------------------------
    useEffect(() => {
        if (!autoplay || !open) return;
        const t = setInterval(() => {
            setIdx((i) => {
                if (i >= total - 1) {
                    setAutoplay(false);
                    return i;
                }
                return i + 1;
            });
        }, 6000);
        return () => clearInterval(t);
    }, [autoplay, open, total]);

    // Fullscreen API -----------------------------------------------------
    const toggleFullscreen = () => {
        const el = rootRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            el.requestFullscreen?.();
            setIsFs(true);
        } else {
            document.exitFullscreen?.();
            setIsFs(false);
        }
    };
    useEffect(() => {
        const onFsChange = () => setIsFs(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", onFsChange);
        return () => document.removeEventListener("fullscreenchange", onFsChange);
    }, []);

    // Touch swipe --------------------------------------------------------
    const touchStart = useRef(null);
    const onTouchStart = (e) => {
        touchStart.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e) => {
        if (touchStart.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStart.current;
        if (Math.abs(dx) > 50) {
            if (dx < 0) setIdx((i) => Math.min(total - 1, i + 1));
            else setIdx((i) => Math.max(0, i - 1));
        }
        touchStart.current = null;
    };

    // Preload the next two slide images for snappy navigation
    useEffect(() => {
        for (let i = 1; i <= 2; i++) {
            const next = slides[idx + i];
            if (next?.data_url) {
                const img = new Image();
                img.src = next.data_url;
            }
        }
    }, [idx, slides]);

    const thumbStrip = useMemo(() => slides.slice(0, 80), [slides]);

    // -- Per-page "Read aloud" narration --------------------------------
    // Revokes any prior blob URL and stops playback. Safe to call any time.
    const stopNarration = () => {
        if (audioRef.current) {
            try {
                audioRef.current.pause();
                audioRef.current.src = "";
            } catch {
                /* noop */
            }
        }
        if (audioUrlRef.current) {
            try {
                URL.revokeObjectURL(audioUrlRef.current);
            } catch {
                /* noop */
            }
            audioUrlRef.current = null;
        }
        setNarration({ status: "idle", page: null });
    };

    // Stop narration + reset zoom/pan/loupe whenever the slide changes or the viewer closes
    useEffect(() => {
        stopNarration();
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setLoupe((l) => ({ ...l, visible: false }));
        setSnipBox(null);
        setSnipResult(null);
        setSnipSummary(null);
        setSnipViewMode("raw");
        setSnipFrameRect(null);
        stopSnippetAudio();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, open]);

    // Reset pan whenever zoom returns to 1 (image fits the frame; no pan needed)
    useEffect(() => {
        if (zoom <= 1) setPan({ x: 0, y: 0 });
    }, [zoom]);

    // Fetch the student's saved note when the viewer opens
    useEffect(() => {
        if (!open || !lesson?.id) return;
        let live = true;
        (async () => {
            try {
                const { data } = await api.get(`/lessons/${lesson.id}/my-note`);
                if (!live) return;
                setNote(data.content || "");
                setNoteLocked(!!data.locked);
                setNoteStatus(data.content ? "saved" : "idle");
            } catch {
                // notes are optional — fail silent
            }
        })();
        return () => {
            live = false;
        };
    }, [open, lesson?.id]);

    // Debounced auto-save of the note
    useEffect(() => {
        if (!open || !lesson?.id || noteLocked) return;
        if (noteStatus === "idle" && !note) return;
        setNoteStatus("saving");
        if (noteSaveTimerRef.current) clearTimeout(noteSaveTimerRef.current);
        noteSaveTimerRef.current = setTimeout(async () => {
            try {
                await api.put(`/lessons/${lesson.id}/my-note`, { content: note });
                setNoteStatus("saved");
            } catch (err) {
                if (err?.response?.status === 423) {
                    setNoteLocked(true);
                    toast.info("Notes for this chapter are locked — the chapter is complete.");
                } else {
                    setNoteStatus("error");
                }
            }
        }, 800);
        return () => {
            if (noteSaveTimerRef.current) clearTimeout(noteSaveTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [note]);

    const toggleNarration = async () => {
        if (!current || !lesson?.id) return;
        // If audio is already loaded for this page, toggle play/pause
        if (audioRef.current && narration.page === current.page) {
            if (audioRef.current.paused) {
                audioRef.current.play();
                setNarration({ status: "playing", page: current.page });
            } else {
                audioRef.current.pause();
                setNarration({ status: "paused", page: current.page });
            }
            return;
        }

        // Fetch fresh TTS for this page (1-based slide.page → 0-based page_idx)
        setNarration({ status: "loading", page: current.page });
        try {
            const pageIdx = Math.max(0, (current.page || 1) - 1);
            const resp = await api.post(
                `/lessons/${lesson.id}/pdf-pages/${pageIdx}/audio`,
                null,
                { responseType: "blob" },
            );
            const blob = resp.data;
            const url = URL.createObjectURL(blob);
            // Clean any previous url we had
            if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = url;
            if (!audioRef.current) audioRef.current = new Audio();
            audioRef.current.src = url;
            audioRef.current.onended = () =>
                setNarration({ status: "idle", page: current.page });
            audioRef.current.onpause = () => {
                // Guard: only reflect user-driven pause (not our stop)
                if (audioRef.current && !audioRef.current.ended) {
                    setNarration((n) =>
                        n.status === "playing" ? { ...n, status: "paused" } : n,
                    );
                }
            };
            await audioRef.current.play();
            setNarration({ status: "playing", page: current.page });
        } catch (err) {
            // Parse the FastAPI error (it arrives as a Blob because responseType=blob)
            let msg = "Could not narrate this page.";
            try {
                const blob = err?.response?.data;
                if (blob && typeof blob.text === "function") {
                    const txt = await blob.text();
                    const parsed = JSON.parse(txt);
                    if (parsed?.detail && typeof parsed.detail === "string") {
                        msg = parsed.detail;
                    }
                }
            } catch {
                /* keep default msg */
            }
            toast.error(msg);
            setNarration({ status: "idle", page: null });
        }
    };

    // -- Snip-text: box-select → extract text → save to notes -----------
    // Converts a screen-pixel rectangle drawn over the frame into image-
    // normalized coordinates (accounting for current zoom + pan), POSTs to
    // the backend, and stores the result in `snipResult` for the popover.
    const runSnipExtract = async (box) => {
        if (!current || !lesson?.id || !frameRef.current) return;
        const fw = frameRef.current.clientWidth || 1;
        const fh = frameRef.current.clientHeight || 1;
        // Reverse the image transform: screen → image-normalized.
        // Image is positioned at frame centre + pan, then scaled by zoom.
        // For a point (px, py) in the frame, the matching image-normalized
        // coordinate is: 0.5 + ((px - fw/2) - pan.x) / (fw * zoom)
        const toNorm = (px, py) => {
            const nx = 0.5 + (px - fw / 2 - pan.x) / (fw * zoom);
            const ny = 0.5 + (py - fh / 2 - pan.y) / (fh * zoom);
            return {
                x: Math.max(0, Math.min(1, nx)),
                y: Math.max(0, Math.min(1, ny)),
            };
        };
        const a = toNorm(box.x, box.y);
        const b = toNorm(box.x + box.w, box.y + box.h);
        const payload = {
            x: Math.min(a.x, b.x),
            y: Math.min(a.y, b.y),
            width: Math.abs(b.x - a.x),
            height: Math.abs(b.y - a.y),
        };
        if (payload.width < 0.01 || payload.height < 0.01) {
            setSnipBox(null);
            toast.info("Draw a bigger box around the text.");
            return;
        }
        setSnipResult({ loading: true, box, text: "", used_ocr: false });
        // Capture the frame's current viewport rect so the popover can float
        // outside the frame — makes it impossible for the slide content to
        // hide the popover.
        if (frameRef.current) {
            setSnipFrameRect(frameRef.current.getBoundingClientRect());
        }
        setSnipSummary(null);
        setSnipViewMode("raw");
        stopSnippetAudio();
        try {
            const pageIdx = Math.max(0, (current.page || 1) - 1);
            const { data } = await api.post(
                `/lessons/${lesson.id}/pdf-pages/${pageIdx}/extract-region`,
                payload,
            );
            setSnipResult({
                loading: false,
                box,
                text: data.text || "",
                used_ocr: !!data.used_ocr,
            });
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Couldn't extract text from that area.";
            setSnipResult({
                loading: false,
                box,
                text: "",
                error: typeof msg === "string" ? msg : "Extraction failed.",
            });
        }
    };

    const copySnipToClipboard = async () => {
        const txt = (snipViewMode === "summary" && snipSummary?.text)
            ? snipSummary.text
            : snipResult?.text;
        if (!txt) return;
        try {
            await navigator.clipboard.writeText(txt);
            toast.success("Copied to clipboard.");
        } catch {
            toast.error("Couldn't copy to clipboard.");
        }
    };

    // -- Snip extras: AI summary + TTS narration ------------------------
    const snippetTextShown = () =>
        snipViewMode === "summary" && snipSummary?.text
            ? snipSummary.text
            : snipResult?.text || "";

    const runSnipSummarize = async () => {
        if (!snipResult?.text || !lesson?.id) return;
        // Toggle: if summary exists, just switch view
        if (snipSummary?.text) {
            setSnipViewMode("summary");
            return;
        }
        setSnipSummary({ loading: true, text: "" });
        setSnipViewMode("summary");
        try {
            const { data } = await api.post(
                `/lessons/${lesson.id}/snippet/summarize`,
                { text: snipResult.text },
            );
            setSnipSummary({ loading: false, text: data.summary || "" });
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Couldn't summarise this snippet.";
            setSnipSummary({
                loading: false,
                text: "",
                error: typeof msg === "string" ? msg : "Summary failed.",
            });
            setSnipViewMode("raw");
            toast.error(typeof msg === "string" ? msg : "Summary failed.");
        }
    };

    const stopSnippetAudio = () => {
        if (snipAudioRef.current) {
            try {
                snipAudioRef.current.pause();
                snipAudioRef.current.src = "";
            } catch { /* noop */ }
        }
        if (snipAudioUrlRef.current) {
            try { URL.revokeObjectURL(snipAudioUrlRef.current); } catch { /* noop */ }
            snipAudioUrlRef.current = null;
        }
        setSnipAudio({ status: "idle" });
    };

    const runSnipReadAloud = async () => {
        const txt = (snippetTextShown() || "").trim();
        if (!txt) {
            toast.info("Nothing to read — snip some text first.");
            return;
        }
        if (!lesson?.id) return;
        // Toggle: pause if already playing
        if (
            snipAudioRef.current &&
            !snipAudioRef.current.paused &&
            snipAudio.status === "playing"
        ) {
            try {
                snipAudioRef.current.pause();
            } catch { /* noop */ }
            setSnipAudio({ status: "idle" });
            return;
        }
        // Fresh fetch
        stopSnippetAudio();
        setSnipAudio({ status: "loading" });
        try {
            const resp = await api.post(
                `/lessons/${lesson.id}/snippet/audio`,
                { text: txt },
                { responseType: "blob" },
            );
            // Axios returns a Blob here. Normalise to an audio/mpeg blob so Safari
            // also plays it reliably regardless of the server-sent Content-Type.
            const raw = resp.data;
            const blob =
                raw instanceof Blob
                    ? new Blob([raw], { type: "audio/mpeg" })
                    : new Blob([raw], { type: "audio/mpeg" });
            const url = URL.createObjectURL(blob);
            snipAudioUrlRef.current = url;
            if (!snipAudioRef.current) snipAudioRef.current = new Audio();
            snipAudioRef.current.src = url;
            snipAudioRef.current.onended = () => setSnipAudio({ status: "idle" });
            snipAudioRef.current.onerror = () => {
                // eslint-disable-next-line no-console
                console.error("snippet audio playback error", snipAudioRef.current?.error);
                toast.error("Couldn't play the narration.");
                setSnipAudio({ status: "idle" });
            };
            await snipAudioRef.current.play();
            setSnipAudio({ status: "playing" });
        } catch (err) {
            let msg = "Couldn't narrate this snippet.";
            try {
                const blob = err?.response?.data;
                if (blob && typeof blob.text === "function") {
                    const parsed = JSON.parse(await blob.text());
                    if (parsed?.detail) msg = parsed.detail;
                } else if (err?.message) {
                    msg = err.message;
                }
            } catch {
                /* keep default */
            }
            // eslint-disable-next-line no-console
            console.error("snippet TTS failed:", err);
            toast.error(msg);
            setSnipAudio({ status: "idle" });
        }
    };

    const appendShownSnippetToNotes = async () => {
        if (!lesson?.id) return;
        if (noteLocked) {
            toast.info("Notes are locked — chapter is complete.");
            return;
        }
        const pageNum = current?.page || 1;
        const shown = snippetTextShown().trim();
        if (!shown) return;
        const header =
            snipViewMode === "summary" && snipSummary?.text
                ? `\n\n> **Page ${pageNum} — summary:**\n${shown}\n`
                : `\n\n> **Page ${pageNum}:** ${shown}\n`;
        setNote((prev) => (prev || "") + header);
        setNotesOpen(true);
        stopSnippetAudio();
        setSnipBox(null);
        setSnipResult(null);
        setSnipSummary(null);
        setSnipViewMode("raw");
        toast.success("Added to your notes.");
    };

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
            if (audioRef.current) {
                try {
                    audioRef.current.pause();
                } catch {
                    /* noop */
                }
            }
            if (snipAudioUrlRef.current) {
                try { URL.revokeObjectURL(snipAudioUrlRef.current); } catch {/* noop */}
            }
            if (snipAudioRef.current) {
                try { snipAudioRef.current.pause(); } catch {/* noop */}
            }
        };
    }, []);

    if (!open) return null;

    // Inline styles for things Tailwind can't express cleanly — `100dvh`
    // falls back to `100vh` on older browsers so the overlay always fills
    // the true visible area even when mobile Chrome's URL bar is visible.
    const shellStyle = {
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100dvh",
        maxHeight: "100vh",
        zIndex: 2147483646, // just below browser UI; ensures we beat any app z-index
        display: "flex",
        flexDirection: "column",
        background: "rgba(0,0,0,0.92)",
        backdropFilter: "blur(6px)",
        overflow: "hidden",
        touchAction: "none", // stop iOS from bouncing the background
    };

    const overlay = (
        <div
            ref={rootRef}
            style={shellStyle}
            data-testid="lesson-slideshow-viewer"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {/* Top bar */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-black/50 text-white flex-shrink-0">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/50">
                        Slideshow
                    </div>
                    <div className="font-display truncate text-sm md:text-base">
                        {lesson?.title || "Lesson"}
                    </div>
                </div>
                <div
                    className="text-xs md:text-sm text-white/80 font-mono bg-white/10 px-2.5 py-1 rounded"
                    data-testid="slideshow-page-indicator"
                >
                    {total ? `${idx + 1} / ${total}` : "—"}
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setAutoplay((a) => !a)}
                    className="text-white/80 hover:text-white hover:bg-white/10"
                    title={autoplay ? "Pause autoplay" : "Play autoplay (every 6s)"}
                    data-testid="slideshow-autoplay-toggle"
                    disabled={!total}
                >
                    {autoplay ? (
                        <Pause className="h-4 w-4" />
                    ) : (
                        <Play className="h-4 w-4" />
                    )}
                </Button>
                {/* Zoom out / reset / in */}
                <div className="hidden md:flex items-center gap-0.5 border-l border-white/10 pl-2 ml-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                            setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))
                        }
                        className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8"
                        title="Zoom out (-)"
                        disabled={zoom <= ZOOM_MIN}
                        data-testid="slideshow-zoom-out"
                    >
                        <ZoomOut className="h-4 w-4" />
                    </Button>
                    <button
                        type="button"
                        onClick={() => setZoom(1)}
                        className="text-white/80 hover:text-white hover:bg-white/10 text-[11px] font-mono px-2 py-0.5 rounded"
                        title="Reset zoom (0)"
                        data-testid="slideshow-zoom-reset"
                    >
                        {Math.round(zoom * 100)}%
                    </button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                            setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))
                        }
                        className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8"
                        title="Zoom in (+)"
                        disabled={zoom >= ZOOM_MAX}
                        data-testid="slideshow-zoom-in"
                    >
                        <ZoomIn className="h-4 w-4" />
                    </Button>
                </div>
                {/* Hand tool — pan mode (H) */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                        setHandTool((v) => {
                            const nv = !v;
                            if (nv) setZoom((z) => (z <= 1 ? 1.3 : z));
                            return nv;
                        })
                    }
                    className={`text-white/80 hover:text-white hover:bg-white/10 h-8 w-8 hidden md:inline-flex ${
                        handTool ? "bg-accent/25 text-accent hover:text-accent" : ""
                    }`}
                    title={handTool ? "Hand tool on — drag to pan (H)" : "Hand tool — drag to pan (H)"}
                    data-testid="slideshow-hand-toggle"
                    disabled={!total}
                >
                    <Hand className="h-4 w-4" />
                </Button>
                {/* Snip text (S) */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        setSnipMode((v) => !v);
                        setSnipResult(null);
                        setSnipBox(null);
                    }}
                    className={`text-white/80 hover:text-white hover:bg-white/10 gap-1.5 hidden md:inline-flex ${
                        snipMode ? "bg-accent/25 text-accent hover:text-accent" : ""
                    }`}
                    title={
                        snipMode
                            ? "Snip text off (S)"
                            : "Snip text — draw a box to extract text to your notes (S)"
                    }
                    data-testid="slideshow-snip-toggle"
                    disabled={!total}
                >
                    <TextCursorInput className="h-4 w-4" />
                    <span className="hidden lg:inline text-xs">Snip</span>
                </Button>
                {/* Magnifier (loupe) toggle */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMagnifier((v) => !v)}
                    className={`text-white/80 hover:text-white hover:bg-white/10 gap-1.5 hidden md:inline-flex ${
                        magnifier ? "bg-accent/25 text-accent hover:text-accent" : ""
                    }`}
                    title={
                        magnifier
                            ? "Turn magnifier off (M)"
                            : "Magnifier — hover over the page to see a loupe (M)"
                    }
                    data-testid="slideshow-magnifier-toggle"
                    disabled={!total}
                >
                    <Search className="h-4 w-4" />
                    <span className="hidden lg:inline text-xs">Loupe</span>
                </Button>
                {/* Notes drawer toggle */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setNotesOpen((v) => !v)}
                    className={`text-white/80 hover:text-white hover:bg-white/10 gap-1.5 ${
                        notesOpen ? "bg-white/15 text-white" : ""
                    }`}
                    title="Toggle notes (write while you learn)"
                    data-testid="slideshow-notes-toggle"
                >
                    <StickyNote className="h-4 w-4" />
                    <span className="hidden md:inline text-xs">Notes</span>
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleFullscreen}
                    className="text-white/80 hover:text-white hover:bg-white/10 hidden md:inline-flex"
                    title={isFs ? "Exit fullscreen (F)" : "Fullscreen (F)"}
                    data-testid="slideshow-fullscreen-toggle"
                >
                    {isFs ? (
                        <Minimize2 className="h-4 w-4" />
                    ) : (
                        <Maximize2 className="h-4 w-4" />
                    )}
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-white/90 hover:text-white hover:bg-white/10"
                    title="Close (Esc)"
                    data-testid="slideshow-close"
                >
                    <X className="h-5 w-5" />
                </Button>
            </div>

            {/* Stage — explicitly shrinks so top/bottom stay pinned */}
            <div
                className="relative flex items-center justify-center px-2 md:px-4 py-3 md:py-6 select-none"
                style={{ flex: "1 1 0", minHeight: 0, overflow: "hidden" }}
            >
                {loading && (
                    <div className="text-white/90 flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p className="text-sm">Loading slides…</p>
                    </div>
                )}
                {!loading && error && (
                    <div
                        className="max-w-md text-center bg-destructive/20 border border-destructive/40 rounded-lg p-6 text-white"
                        data-testid="slideshow-error"
                    >
                        <AlertTriangle className="h-6 w-6 mx-auto mb-3 text-destructive" />
                        <p className="text-sm">{error}</p>
                    </div>
                )}
                {!loading && !error && current && (
                    <div
                        ref={frameRef}
                        className="relative rounded-xl bg-white ring-1 ring-white/15 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)]"
                        style={{
                            // Framed "presenter window" — the frame's own size is
                            // locked (it never jumps during zoom). Zoom + pan are
                            // contained entirely inside via overflow:hidden, so the
                            // surrounding UI stays stable. Sized wider for better
                            // readability on desktop.
                            height: "min(calc(100dvh - 120px), 92vh)",
                            aspectRatio: "1200 / 1400",
                            maxWidth: "min(98vw, 1600px)",
                            overflow: "hidden",
                            cursor: snipMode
                                ? "crosshair"
                                : magnifier
                                ? "crosshair"
                                : handTool || zoom > 1
                                ? panRef.current.dragging
                                    ? "grabbing"
                                    : "grab"
                                : "default",
                            touchAction: zoom > 1 || snipMode ? "none" : "auto",
                        }}
                        onMouseDown={(e) => {
                            // Snip mode: start drawing a rectangle
                            if (snipMode && frameRef.current) {
                                const rect = frameRef.current.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;
                                snipDragRef.current = {
                                    dragging: true,
                                    startX: x,
                                    startY: y,
                                };
                                setSnipBox({ x, y, w: 0, h: 0 });
                                setSnipResult(null);
                                return;
                            }
                            if (magnifier) return;
                            // Pan requires either hand tool or zoom > 1
                            if (!handTool && zoom <= 1) return;
                            panRef.current = {
                                dragging: true,
                                startX: e.clientX,
                                startY: e.clientY,
                                baseX: pan.x,
                                baseY: pan.y,
                            };
                        }}
                        onMouseMove={(e) => {
                            // Snip-rect drag
                            if (snipDragRef.current.dragging && frameRef.current) {
                                const rect = frameRef.current.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;
                                const sx = snipDragRef.current.startX;
                                const sy = snipDragRef.current.startY;
                                setSnipBox({
                                    x: Math.min(sx, x),
                                    y: Math.min(sy, y),
                                    w: Math.abs(x - sx),
                                    h: Math.abs(y - sy),
                                });
                                return;
                            }
                            // Drag-to-pan when zoomed or hand tool on
                            if (panRef.current.dragging) {
                                const dx = e.clientX - panRef.current.startX;
                                const dy = e.clientY - panRef.current.startY;
                                setPan({
                                    x: panRef.current.baseX + dx,
                                    y: panRef.current.baseY + dy,
                                });
                                return;
                            }
                            // Magnifier loupe position + background offset
                            if (magnifier && frameRef.current) {
                                const rect = frameRef.current.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;
                                // Keep loupe fully inside the frame
                                const lx = Math.max(
                                    LOUPE_SIZE / 2,
                                    Math.min(rect.width - LOUPE_SIZE / 2, x),
                                );
                                const ly = Math.max(
                                    LOUPE_SIZE / 2,
                                    Math.min(rect.height - LOUPE_SIZE / 2, y),
                                );
                                setLoupe({
                                    visible: true,
                                    x: lx,
                                    y: ly,
                                    bgX: x * LOUPE_SCALE - LOUPE_SIZE / 2,
                                    bgY: y * LOUPE_SCALE - LOUPE_SIZE / 2,
                                });
                            }
                        }}
                        onMouseUp={() => {
                            // Finalise snip-rect → call extractor
                            if (snipDragRef.current.dragging) {
                                snipDragRef.current.dragging = false;
                                if (snipBox && snipBox.w >= 20 && snipBox.h >= 20) {
                                    runSnipExtract(snipBox);
                                } else {
                                    setSnipBox(null);
                                }
                                return;
                            }
                            panRef.current.dragging = false;
                        }}
                        onMouseLeave={() => {
                            panRef.current.dragging = false;
                            if (snipDragRef.current.dragging) {
                                snipDragRef.current.dragging = false;
                                setSnipBox(null);
                            }
                            if (magnifier) {
                                setLoupe((l) => ({ ...l, visible: false }));
                            }
                        }}
                        onMouseEnter={() => {
                            if (magnifier) setLoupe((l) => ({ ...l, visible: true }));
                        }}
                        data-testid="slideshow-frame"
                    >
                        <img
                            src={current.data_url}
                            alt={`Page ${current.page}`}
                            className="select-none"
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`,
                                transformOrigin: "center center",
                                transition: panRef.current.dragging
                                    ? "none"
                                    : "transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                                willChange: "transform",
                            }}
                            data-testid="slideshow-current-image"
                            draggable={false}
                        />
                        {/* Snip rectangle (while dragging) */}
                        {snipMode && snipBox && (
                            <div
                                className="pointer-events-none absolute border-2 border-accent"
                                style={{
                                    left: snipBox.x,
                                    top: snipBox.y,
                                    width: snipBox.w,
                                    height: snipBox.h,
                                    backgroundColor: "rgba(212, 161, 64, 0.15)",
                                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.25)",
                                }}
                                data-testid="slideshow-snip-box"
                            />
                        )}
                        {/* Snip result popover */}
                        {/* Snip popover is rendered below, outside this frame div,
                         * so it can float free with position:fixed. */}
                        {/* Magnifier loupe — circular viewport showing 2.4× zoomed source */}
                        {magnifier && loupe.visible && (
                            <div
                                className="pointer-events-none rounded-full ring-2 ring-accent/80 shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
                                style={{
                                    position: "absolute",
                                    left: loupe.x - LOUPE_SIZE / 2,
                                    top: loupe.y - LOUPE_SIZE / 2,
                                    width: LOUPE_SIZE,
                                    height: LOUPE_SIZE,
                                    backgroundImage: `url(${current.data_url})`,
                                    backgroundRepeat: "no-repeat",
                                    // Magnify the source image by LOUPE_SCALE relative to
                                    // the frame — background-size keyed to the frame box so
                                    // the loupe stays aligned regardless of viewport.
                                    backgroundSize: `${
                                        (frameRef.current?.clientWidth || 0) * LOUPE_SCALE
                                    }px ${
                                        (frameRef.current?.clientHeight || 0) * LOUPE_SCALE
                                    }px`,
                                    backgroundPosition: `-${loupe.bgX}px -${loupe.bgY}px`,
                                    backgroundColor: "#fff",
                                }}
                                data-testid="slideshow-loupe"
                            />
                        )}
                        {/* Mini-map — appears when zoomed. Shows the whole page with
                         * an accent-colored rectangle indicating the currently visible
                         * region. Helps the student keep orientation while panning. */}
                        {zoom > 1 && current && (() => {
                            const fw = frameRef.current?.clientWidth || 1;
                            const fh = frameRef.current?.clientHeight || 1;
                            // Fraction of the image that's currently visible inside the frame
                            const vwFrac = Math.min(1, 1 / zoom);
                            const vhFrac = Math.min(1, 1 / zoom);
                            // Top-left of the visible window in image-normalized coords
                            const xFrac = Math.max(
                                0,
                                Math.min(
                                    1 - vwFrac,
                                    0.5 - 0.5 / zoom - pan.x / (fw * zoom),
                                ),
                            );
                            const yFrac = Math.max(
                                0,
                                Math.min(
                                    1 - vhFrac,
                                    0.5 - 0.5 / zoom - pan.y / (fh * zoom),
                                ),
                            );
                            return (
                                <div
                                    className="absolute bottom-3 right-3 rounded-md overflow-hidden bg-black/70 ring-1 ring-white/30 shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200"
                                    style={{
                                        width: 120,
                                        aspectRatio: "1200 / 1553",
                                    }}
                                    data-testid="slideshow-minimap"
                                >
                                    <img
                                        src={current.data_url}
                                        alt=""
                                        className="w-full h-full object-contain opacity-90"
                                        draggable={false}
                                    />
                                    {/* Viewport indicator */}
                                    <div
                                        className="absolute border-2 border-accent pointer-events-none"
                                        style={{
                                            left: `${xFrac * 100}%`,
                                            top: `${yFrac * 100}%`,
                                            width: `${vwFrac * 100}%`,
                                            height: `${vhFrac * 100}%`,
                                            backgroundColor: "rgba(212, 161, 64, 0.18)",
                                            boxShadow:
                                                "0 0 0 9999px rgba(0,0,0,0.35)",
                                            transition: panRef.current.dragging
                                                ? "none"
                                                : "left 240ms cubic-bezier(0.22,0.61,0.36,1), top 240ms cubic-bezier(0.22,0.61,0.36,1), width 240ms cubic-bezier(0.22,0.61,0.36,1), height 240ms cubic-bezier(0.22,0.61,0.36,1)",
                                        }}
                                        data-testid="slideshow-minimap-viewport"
                                    />
                                    {/* Zoom label */}
                                    <div className="absolute top-1 left-1 text-[9px] font-mono bg-black/70 text-white/90 px-1.5 py-0.5 rounded">
                                        {Math.round(zoom * 100)}%
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Edge click zones for big, always-findable prev/next */}
                {!loading && total > 0 && (
                    <>
                        <button
                            type="button"
                            onClick={() => setIdx((i) => Math.max(0, i - 1))}
                            disabled={idx === 0}
                            aria-label="Previous page"
                            className="group absolute left-0 top-0 h-full w-[20%] flex items-center justify-start pl-2 md:pl-4 disabled:opacity-30 disabled:cursor-not-allowed transition"
                            data-testid="slideshow-prev"
                        >
                            <span className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-black/50 group-hover:bg-black/75 text-white flex items-center justify-center shadow-lg ring-1 ring-white/20 transition">
                                <ChevronLeft className="h-7 w-7" />
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                setIdx((i) => Math.min(total - 1, i + 1))
                            }
                            disabled={idx >= total - 1}
                            aria-label="Next page"
                            className="group absolute right-0 top-0 h-full w-[20%] flex items-center justify-end pr-2 md:pr-4 disabled:opacity-30 disabled:cursor-not-allowed transition"
                            data-testid="slideshow-next"
                        >
                            <span className="h-12 w-12 md:h-14 md:w-14 rounded-full bg-black/50 group-hover:bg-black/75 text-white flex items-center justify-center shadow-lg ring-1 ring-white/20 transition">
                                <ChevronRight className="h-7 w-7" />
                            </span>
                        </button>
                    </>
                )}
            </div>

            {/* Notes drawer — slides in from the right over the stage */}
            {notesOpen && (
                <div
                    className="absolute right-0 top-[57px] md:top-[65px] bottom-[50px] md:bottom-[70px] w-full md:w-[360px] lg:w-[400px] bg-neutral-900/95 border-l border-white/10 flex flex-col animate-in slide-in-from-right duration-200"
                    data-testid="slideshow-notes-drawer"
                >
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 text-white flex-shrink-0">
                        <StickyNote className="h-4 w-4 text-accent" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold leading-tight truncate">
                                My notes for this lesson
                            </div>
                            <div className="text-[11px] text-white/50">
                                {noteLocked
                                    ? "Locked — chapter complete"
                                    : "Saves automatically as you type. Markdown supported."}
                            </div>
                        </div>
                        {noteStatus === "saving" ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-white/60" />
                        ) : noteStatus === "saved" && !noteLocked ? (
                            <span className="text-[11px] text-primary-foreground/80 flex items-center gap-1">
                                <Check className="h-3 w-3 text-accent" /> Saved
                            </span>
                        ) : null}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setNotesOpen(false)}
                            className="text-white/60 hover:text-white hover:bg-white/10 h-7 w-7"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        readOnly={noteLocked}
                        placeholder={
                            noteLocked
                                ? "This chapter is complete. Your notes are locked."
                                : "Jot down key points, questions, things to revise later…"
                        }
                        className="flex-1 resize-none bg-transparent text-white/90 px-4 py-3 text-sm leading-relaxed outline-none placeholder:text-white/35 font-mono"
                        data-testid="slideshow-notes-textarea"
                    />
                    <div className="px-4 py-2 border-t border-white/10 text-[11px] text-white/45">
                        Supports Markdown: <code>## heading</code>, <code>- bullet</code>,{" "}
                        <code>**bold**</code>
                    </div>
                </div>
            )}

            {/* Bottom bar */}
            {total > 0 && !error && (
                <div className="border-t border-white/10 bg-black/50 px-3 md:px-4 py-2 md:py-3 flex-shrink-0">
                    <div
                        className="h-1 bg-white/15 rounded-full overflow-hidden mb-2"
                        data-testid="slideshow-progress"
                    >
                        <div
                            className="h-full bg-accent transition-all duration-300"
                            style={{ width: `${((idx + 1) / total) * 100}%` }}
                        />
                    </div>
                    {showThumbs ? (
                        <div className="flex gap-1.5 overflow-x-auto py-1 scrollbar-thin">
                            {thumbStrip.map((s, i) => (
                                <button
                                    key={s.page}
                                    type="button"
                                    onClick={() => setIdx(i)}
                                    aria-label={`Go to page ${s.page}`}
                                    className={`flex-shrink-0 h-10 w-8 md:h-12 md:w-9 rounded-sm overflow-hidden border-2 transition ${
                                        i === idx
                                            ? "border-accent shadow-lg shadow-accent/40"
                                            : "border-white/20 hover:border-white/60 opacity-70 hover:opacity-100"
                                    }`}
                                    data-testid={`slideshow-thumb-${i}`}
                                >
                                    <img
                                        src={s.data_url}
                                        alt=""
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                    />
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-between text-[11px] text-white/60">
                            <span>
                                ← / → navigate · Space advances · + / - / 0 zoom · H hand · M magnify · S snip · Esc close
                            </span>
                            {total > 80 && (
                                <span className="font-mono">{total} slides</span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ======================================================
             *  Floating Snip popover — rendered OUTSIDE the frame so
             *  it can anchor to the right/left of the snip rectangle
             *  regardless of frame size, with a bubble-opening motion.
             *  Uses `position: fixed` relative to the viewport (the
             *  parent portal already pins everything to the viewport).
             *  ====================================================== */}
            <AnimatePresence>
                {snipMode && snipResult && snipFrameRect && (() => {
                    const POP_W = 360;
                    // Body scroll cap only — let the popover grow to fit its
                    // action buttons so the bottom row is never clipped.
                    const BODY_MAX_H = 260;
                    // Reasonable upper bound when clamping vertical position —
                    // we don't render into this fixed height; we reserve it so
                    // the popover doesn't get placed too close to the bottom.
                    const RESERVE_H = 460;
                    const PAD = 16;
                    const vw = window.innerWidth;
                    const vh = window.innerHeight;
                    const box = snipResult.box || { x: 0, y: 0, w: 0, h: 0 };
                    const snipTop = snipFrameRect.top + box.y;
                    const snipLeft = snipFrameRect.left + box.x;
                    // Prefer right of frame; else left; else below-center
                    let left;
                    let originX = "left";
                    const rightRoom = vw - snipFrameRect.right;
                    const leftRoom = snipFrameRect.left;
                    if (rightRoom >= POP_W + PAD * 2) {
                        left = snipFrameRect.right + PAD;
                        originX = "left";
                    } else if (leftRoom >= POP_W + PAD * 2) {
                        left = snipFrameRect.left - POP_W - PAD;
                        originX = "right";
                    } else {
                        left = Math.max(PAD, Math.min(snipLeft, vw - POP_W - PAD));
                        originX = "center";
                    }
                    // Vertically align with the snip rect center, clamp so the
                    // popover (roughly RESERVE_H tall) stays on-screen.
                    const idealTop = snipTop + box.h / 2 - RESERVE_H / 2;
                    const top = Math.max(
                        PAD,
                        Math.min(idealTop, vh - RESERVE_H - PAD),
                    );
                    return (
                        <motion.div
                            key="snip-popover"
                            initial={{ opacity: 0, scale: 0.7, y: 8 }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                                // Gentle floating-cloud bob once the popover
                                // has settled. Small amplitude so it feels
                                // lively but never distracting.
                                y: [0, -4, 0, -4, 0],
                            }}
                            exit={{ opacity: 0, scale: 0.9, y: 6 }}
                            transition={{
                                opacity: { duration: 0.18, ease: "easeOut" },
                                scale: {
                                    type: "spring",
                                    stiffness: 420,
                                    damping: 26,
                                    mass: 0.8,
                                },
                                y: {
                                    repeat: Infinity,
                                    repeatType: "loop",
                                    duration: 6,
                                    ease: "easeInOut",
                                    delay: 0.35,
                                },
                            }}
                            className="rounded-2xl border border-border bg-card text-foreground shadow-[0_28px_64px_-12px_rgba(0,0,0,0.6)] overflow-hidden flex flex-col"
                            style={{
                                position: "fixed",
                                left,
                                top,
                                width: POP_W,
                                maxHeight: `calc(100vh - ${PAD * 2}px)`,
                                transformOrigin: `${originX} center`,
                                zIndex: 2147483647,
                            }}
                            data-testid="slideshow-snip-popover"
                        >
                            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-primary/5">
                                {snipViewMode === "summary" ? (
                                    <Sparkles className="h-4 w-4 text-accent flex-shrink-0" />
                                ) : (
                                    <TextCursorInput className="h-4 w-4 text-accent flex-shrink-0" />
                                )}
                                <div className="text-sm font-semibold flex-1 text-primary">
                                    {snipViewMode === "summary"
                                        ? "AI summary"
                                        : "Extracted text"}
                                </div>
                                {snipResult.used_ocr && snipViewMode === "raw" && (
                                    <span className="text-[10px] uppercase tracking-wider bg-accent/20 text-accent px-1.5 py-0.5 rounded">
                                        OCR
                                    </span>
                                )}
                                <button
                                    onClick={() => {
                                        stopSnippetAudio();
                                        setSnipResult(null);
                                        setSnipBox(null);
                                        setSnipSummary(null);
                                        setSnipViewMode("raw");
                                        setSnipFrameRect(null);
                                    }}
                                    className="text-muted-foreground hover:text-foreground transition"
                                    title="Close"
                                    data-testid="slideshow-snip-close"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div
                                className={`px-4 py-3 overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap text-foreground ${
                                    snipViewMode === "summary"
                                        ? ""
                                        : "font-mono text-[13px]"
                                }`}
                                style={{ maxHeight: BODY_MAX_H }}
                                data-testid={`slideshow-snip-body-${snipViewMode}`}
                            >
                                {snipResult.loading ? (
                                    <div className="flex items-center gap-2 py-2 text-muted-foreground">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        Reading text…
                                    </div>
                                ) : snipResult.error ? (
                                    <div className="text-destructive text-[13px]">
                                        {snipResult.error}
                                    </div>
                                ) : snipViewMode === "summary" ? (
                                    snipSummary?.loading ? (
                                        <div className="flex items-center gap-2 py-2 text-muted-foreground">
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            Thinking…
                                        </div>
                                    ) : (
                                        snipSummary?.text || "(no summary)"
                                    )
                                ) : (
                                    snipResult.text || "(no text)"
                                )}
                            </div>
                            {!snipResult.loading &&
                                !snipResult.error &&
                                snipResult.text && (
                                    <>
                                        <div className="flex gap-2 px-4 py-2 border-t border-border">
                                            {snipViewMode === "summary" &&
                                            snipSummary?.text ? (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => setSnipViewMode("raw")}
                                                    className="gap-1.5 text-xs"
                                                    title="Show the original extracted text"
                                                    data-testid="slideshow-snip-show-raw"
                                                >
                                                    <FileText className="h-3.5 w-3.5" />
                                                    Show original
                                                </Button>
                                            ) : (
                                                snipResult.text.length >= 30 && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={runSnipSummarize}
                                                        disabled={snipSummary?.loading}
                                                        className="gap-1.5 text-xs text-accent hover:text-accent hover:bg-accent/10"
                                                        title="Generate a 2-3 bullet summary"
                                                        data-testid="slideshow-snip-summarize"
                                                    >
                                                        {snipSummary?.loading ? (
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        ) : (
                                                            <Sparkles className="h-3.5 w-3.5" />
                                                        )}
                                                        {snipSummary?.text
                                                            ? "Show summary"
                                                            : "Summarize"}
                                                    </Button>
                                                )
                                            )}
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={runSnipReadAloud}
                                                disabled={snipAudio.status === "loading"}
                                                className={`gap-1.5 text-xs ${
                                                    snipAudio.status === "playing"
                                                        ? "text-accent hover:text-accent"
                                                        : ""
                                                }`}
                                                title={
                                                    snipAudio.status === "playing"
                                                        ? "Pause narration"
                                                        : "Read this aloud"
                                                }
                                                data-testid="slideshow-snip-narrate"
                                            >
                                                {snipAudio.status === "loading" ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : snipAudio.status === "playing" ? (
                                                    <VolumeX className="h-3.5 w-3.5" />
                                                ) : (
                                                    <Volume2 className="h-3.5 w-3.5" />
                                                )}
                                                {snipAudio.status === "playing"
                                                    ? "Pause"
                                                    : "Read aloud"}
                                            </Button>
                                        </div>
                                        <div className="flex gap-2 px-4 py-3 border-t border-border bg-muted/30">
                                            <Button
                                                size="sm"
                                                onClick={appendShownSnippetToNotes}
                                                disabled={noteLocked}
                                                className="gap-1.5 flex-1"
                                                title={
                                                    noteLocked
                                                        ? "Notes are locked — chapter complete"
                                                        : "Append to my notes"
                                                }
                                                data-testid="slideshow-snip-save"
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                Add to notes
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={copySnipToClipboard}
                                                className="gap-1.5"
                                                data-testid="slideshow-snip-copy"
                                            >
                                                <Copy className="h-3.5 w-3.5" />
                                                Copy
                                            </Button>
                                        </div>
                                    </>
                                )}
                        </motion.div>
                    );
                })()}
            </AnimatePresence>
        </div>
    );

    // Portal to <body> so `position: fixed` can't be trapped by any parent
    // with a `transform` / `filter` / `will-change` in the React tree.
    if (typeof document === "undefined") return null;
    return createPortal(overlay, document.body);
}
